import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAppSelector } from '../../app/hooks';
import { memberColors } from './chart-colors';
import { median, sigma } from '../../utilities/math'; // used inside NormalPDFPlugin

const NUM_BINS = 20;

const WhiteBgPlugin = {
    id: 'WhiteBg',
    beforeDraw: function (chart: any) {
        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
    },
};

const NormalPDFPlugin = {
    id: 'NormalPDF',
    afterDraw: function (chart: any) {
        const opts = chart.options.plugins?.normalPDF;
        if (!opts || !opts.enabled) return;

        // collect scores only from visible datasets
        const visibleScores: number[] = chart.data.datasets.flatMap((ds: any, i: number) => {
            const meta = chart.getDatasetMeta(i);
            if (meta.hidden) return [];
            return opts.memberScores[ds.label] ?? [];
        });

        if (visibleScores.length < 2) return;

        const mu = median(visibleScores);
        const sig = sigma(visibleScores);
        if (sig === 0) return;

        const N = visibleScores.length;
        const { binWidth, firstBinCenter, lastBinCenter, numBins } = opts;
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;

        const firstPx = xScale.getPixelForValue(0);
        const lastPx = xScale.getPixelForValue(numBins - 1);
        const scoreRange = lastBinCenter - firstBinCenter;

        const pixelToScore = (px: number): number =>
            firstBinCenter + ((px - firstPx) / (lastPx - firstPx)) * scoreRange;

        const scaledPDF = (x: number): number => {
            const z = (x - mu) / sig;
            return (1 / (sig * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z) * N * binWidth;
        };

        // draw curve
        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
        ctx.lineWidth = 2;
        const steps = 500;
        let started = false;
        for (let i = 0; i <= steps; i++) {
            const px = chartArea.left + (i / steps) * (chartArea.right - chartArea.left);
            const score = pixelToScore(px);
            const py = yScale.getPixelForValue(scaledPDF(score));
            if (!started) { ctx.moveTo(px, py); started = true; }
            else ctx.lineTo(px, py);
        }
        ctx.stroke();

        // vertical line at median
        const meanPx = firstPx + ((mu - firstBinCenter) / scoreRange) * (lastPx - firstPx);
        const pdfAtMean = scaledPDF(mu);
        const pdfPeakPy = yScale.getPixelForValue(pdfAtMean);

        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.lineWidth = 2;
        ctx.moveTo(meanPx, pdfPeakPy);
        ctx.lineTo(meanPx, chartArea.bottom);
        ctx.stroke();

        // median label
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.textAlign = 'center';
        ctx.fillText(mu.toFixed(2), meanPx, pdfPeakPy - 6);

        // sigma line
        const sigmaPx = firstPx + ((mu + sig - firstBinCenter) / scoreRange) * (lastPx - firstPx);
        const sigmaLinePy = yScale.getPixelForValue(scaledPDF(mu + sig));
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.lineWidth = 2;
        ctx.moveTo(meanPx, sigmaLinePy);
        ctx.lineTo(sigmaPx, sigmaLinePy);
        ctx.stroke();

        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.textAlign = 'center';
        ctx.fillText(sig.toFixed(2), meanPx + (sigmaPx - meanPx) / 2, sigmaLinePy - 6);

        ctx.restore();
    },
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function buildBinEdges(allScores: number[]): number[] {
    const min = Math.min(...allScores);
    const max = Math.max(...allScores);
    const range = max - min || 1;
    const binWidth = range / NUM_BINS;
    const edges: number[] = [];
    for (let i = 0; i <= NUM_BINS; i++) edges.push(min + i * binWidth);
    return edges;
}

function scoreToBinIndex(score: number, edges: number[]): number {
    for (let i = 0; i < edges.length - 1; i++) {
        if (score >= edges[i] && score < edges[i + 1]) return i;
    }
    return edges.length - 2;
}

function NFitGroupHistogramChart() {
    const coachMembers = useAppSelector((state) => state.coachMembers.members);

    const membersWithHistory = coachMembers
        .filter(m => m.history && m.history.length > 0)
        .map((m, i) => ({
            label: m.firstName,
            scores: m.history.map(h => h.nFit),
            colorIndex: i % memberColors.length,
        }));

    if (membersWithHistory.length === 0) return null;

    const allScores = membersWithHistory.flatMap(m => m.scores);
    const binEdges = buildBinEdges(allScores);
    const memberScores = Object.fromEntries(membersWithHistory.map(m => [m.label, m.scores]));
    const binLabels = binEdges.slice(0, NUM_BINS).map((edge, i) =>
        ((edge + binEdges[i + 1]) / 2).toFixed(2)
    );

    const datasets = membersWithHistory.map(member => {
        const counts = new Array(NUM_BINS).fill(0);
        member.scores.forEach(score => counts[scoreToBinIndex(score, binEdges)]++);
        return {
            label: member.label,
            data: counts,
            backgroundColor: memberColors[member.colorIndex].backgroundColor,
            borderColor: memberColors[member.colorIndex].borderColor,
            borderWidth: 1,
            stack: 'group',
        };
    });

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: true,
                position: 'top' as const,
                labels: { usePointStyle: true },
            },
            title: { display: false },
            normalPDF: {
                enabled: true,
                memberScores,
                binWidth: binEdges.length > 1 ? binEdges[1] - binEdges[0] : 1,
                firstBinCenter: (binEdges[0] + binEdges[1]) / 2,
                lastBinCenter: (binEdges[NUM_BINS - 1] + binEdges[NUM_BINS]) / 2,
                numBins: NUM_BINS,
            },
        },
        scales: {
            x: {
                stacked: true,
                ticks: {
                    font: { family: 'Inter, sans-serif', size: 10 },
                    maxTicksLimit: 10,
                },
                grid: { display: false },
            },
            y: {
                stacked: true,
                ticks: {
                    font: { family: 'Inter, sans-serif', size: 10 },
                    stepSize: 1,
                },
                grid: { display: false },
                beginAtZero: true,
            },
        },
    };

    return (
        <Bar options={options} data={{ labels: binLabels, datasets }} plugins={[WhiteBgPlugin, NormalPDFPlugin]} />
    );
}

export default NFitGroupHistogramChart;
